/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo, memo, useRef } from 'react';
import { Navigate, useParams, useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import { store } from 'redux/store';

import PageContainer from '@components/page-container';
import PageHeader from '@components/page-header';
import PatentSiteDetailsInfo from '../parent-site-details-info';
import SiteCard from '@components/site-card';
import NewTabs from '@components/new-tabs';
import SiteListing from '@components/site-listing';
import SiteBookmark from '@components/site-listing/site-bookmark';
import LoadingRound from '@components/loading-round/loading-round';
import EquipmentListing from '@components/equipment-listing';
import StructureListing from '@components/structure-listing';
import PipelineListing from '@components/pipeline-listing';
import WorkOrder from '../../site-details/tasks/work-order';
import ParentSite from '@components/site-parent-site/tasks';
import MapListing from '@components/map-listing';
import BookmarkErrorScreen from '@components/bookmark-error-screen';
import { moreInfoDropdownList, tabsItemMap } from '@components/site-parent-site/helper';

import { getAppDispatch } from '@utilities/dispatch.util';
import { backHistoryActions } from '@redux/slices/back-history';
import useWindowDimensions from '@hooks/useWindowDimensions';
import { filterActions } from '@redux/slices/filters';
import { PERMISSIONS } from 'permissions/constants';
import { log } from '@utilities/logger.util';
import { siteMapActions } from '@redux/slices/site-map';

import './parent-site.scss';
import style from './style.module.scss';

import { initWebWorker } from 'webworker';
import SiteCardMenuBottom from '@components/site-card-menu-bottom';
import useNetworkStatus from '@hooks/useNetworkStatus';
import BookmarksService from '@services/bookmarks.service';
import { bookmarksActions } from '@redux/slices/bookmarks';
import DeleteModal from '@components/modal/variants/delete-modal';
import { useToastContext } from '@context/toaster.context';

const ParentSiteDetails = () => {
  const { width } = useWindowDimensions();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const dispatch = getAppDispatch();
  const navigate = useNavigate();
  const [isOnline] = useNetworkStatus();
  const { ToasterUtil } = useToastContext();

  const { id } = useParams();
  const bookmarkName = searchParams.get('bookmarkName');
  const bookmarkId = searchParams.get('bookmarkId');
  const isBookmarkAtAGlance = searchParams.get('isBookmarkAtAGlance') || false;

  const bookmarkTab = searchParams.get('bookmarkTab');
  const templateString = searchParams.get('siteTemplates') || '';
  const currentOpenTaskTab = searchParams.get('currentOpenTaskTab') || '';
  const selectedTaskType = searchParams.get('selectedTaskType');

  const { picklistsMap = {} } = useSelector((state) => state.picklists);

  const swipeAbleMenuRef = useRef(null);
  const mainDivRef = useRef(null);
  const siteCardRef = useRef(null);
  const assetDetailPageHeaderRef = useRef(null);

  const [tab, setTab] = useState('0');
  const [open, setOpen] = useState(false);
  const [moreInfo, setMoreInfo] = useState(false);
  const [allTask, setAllTask] = useState(false);
  const [checks, setChecks] = useState(false);
  const [inspections, setInspections] = useState(false);
  const [workOrder, setWorkOrder] = useState(false);
  const [workRequest, setWorkRequest] = useState(false);
  const [hseEvent, setHseEvent] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [deleteBookmark, setDeleteBookmark] = useState(false);
  const [newOrEditAssets, setNewOrEditAssets] = useState(false);

  const [parentSiteDetails, setParentSiteDetails] = useState({});
  const [isFilterActive, setFilterActive] = useState(width < 768 ? false : true);
  const [isFilterActiveForEquipments, setFilterActiveForEquipments] = useState(
    width < 768 ? false : true
  );
  const [isFilterActiveForPipeline, setFilterActiveForPipeline] = useState(
    width < 768 ? false : true
  );
  const [isFilterActiveForStructure, setFilterActiveForStructure] = useState(
    width < 768 ? false : true
  );
  const [isFilterActiveForChildSites, setFilterActiveForChildSites] = useState(
    width < 768 ? false : true
  );
  const [isLoading, setIsLoading] = useState(true);

  const parentSiteDetailsInfo = useSelector((state) => state.backHistory.parentSiteDetails);
  const {
    tasksFilterCount = 0,
    structureFilterCount = 0,
    equipmentFilterCount,
    pipelineFilterCount = 0,
    sitesFilterCount = 0
  } = useSelector((state) => state.filters);
  const { tasksList: reduxTasksList = [], selectTask } = useSelector((state) => state.tasks);
  const { inspectionsList } = useSelector((state) => state.inspection);
  const { HSEEventList } = useSelector((state) => state.hseEvents);
  const { checksList } = useSelector((state) => state.checks);
  const { syncedNewAndEditedEquipments = [] } = useSelector((state) => state.cache);
  const { utFormDownloadingLoading } = useSelector((state) => state.utFormData) || {};

  const { showMapView = false, selectedSiteIdForMapView } =
    useSelector((state) => state.siteMap) || {};

  useEffect(() => {
    if (currentOpenTaskTab) {
      if (currentOpenTaskTab === 'allTask') setAllTask(true);
      else if (currentOpenTaskTab === 'workOrder') setWorkOrder(true);
      else if (currentOpenTaskTab === 'inspections') setInspections(true);
      else if (currentOpenTaskTab === 'workRequest') setWorkRequest(true);
      else if (currentOpenTaskTab === 'hseEvent') setHseEvent(true);
      else if (currentOpenTaskTab === 'check') setChecks(true);
      else if (currentOpenTaskTab === 'newOrEditAssets') setNewOrEditAssets(true);
    }
  }, [currentOpenTaskTab]);

  const templatesIds = useMemo(
    () => templateString.split(',').filter((el) => el),
    [templateString]
  );

  useEffect(() => {
    if (!_.isEmpty(parentSiteDetailsInfo)) {
      parentSiteDetailsInfo?.parentSiteDetailsTab &&
        setTab(parentSiteDetailsInfo?.parentSiteDetailsTab || '0');
      parentSiteDetailsInfo?.workOrder && setWorkOrder(parentSiteDetailsInfo?.workOrder);
      parentSiteDetailsInfo?.newOrEditAssets &&
        setNewOrEditAssets(parentSiteDetailsInfo?.newOrEditAssets);
    }
  }, [parentSiteDetailsInfo]);

  useEffect(() => {
    if (utFormDownloadingLoading) {
      return;
    }

    initWebWorker({
      args: {
        store: { ...store.getState() },
        siteId: id,
        templatesIds,
        equipmentsWithWO: true,
        getWorkRequestData: true,
        selectedTaskType
      },
      type: 'get-parent-site-complete-details'
    })
      .then((data) => {
        setParentSiteDetails(data?.result);
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
      });
  }, [
    id,
    templatesIds,
    reduxTasksList,
    inspectionsList,
    HSEEventList,
    checksList,
    selectedTaskType,
    syncedNewAndEditedEquipments,
    utFormDownloadingLoading
  ]);

  useEffect(() => {
    if (!isLoading) {
      !dataPoints && log(unit, distance, children);
      if (!sitesLoading && !parentSite?.siteId) return <Navigate to={'/sites'} />;
    }
  }, [isLoading, parentSiteDetails]);

  const {
    sitesLoading,
    name,
    tasks,
    allDoneTasks,
    allWorkOrders,
    allDoneWorkOrders,
    allInspections,
    allDoneInspections,
    allChecks,
    allDoneChecks,
    isCheckOverDue,
    isOverDue,
    isWorkOrderOverDue,
    isInspectionOverDue,
    status,
    area,
    equipments,
    equipmentsMap,
    dataPoints,
    unit,
    distance,
    children,
    parentSite,

    dataPointsMap,
    MoreInfoDataPointsMap,
    equipmentsWithWO,
    structures,
    structuresMap,
    structureAllFieldData,
    pipelines = [],
    link,
    newWorkRequestList,
    hseEvents,
    coordinatesListOfChild,
    equipmentCount,
    structureCount,
    pipelineCount,
    allOnDemandRecordCount,
    allEditEquipmentAssets
  } = useMemo(() => {
    return parentSiteDetails;
  }, [parentSiteDetails]);

  const arrayText = useMemo(() => {
    return moreInfoDropdownList({ setMoreInfo, setOpen, setOpenModal, link, width });
  }, [link, width]);

  const itemsMap = useMemo(() => {
    return tabsItemMap({
      isLoading,
      tasks,
      children,
      equipments,
      structures,
      pipelines,
      onDemandRecordList: allOnDemandRecordCount
    });
  }, [isLoading, allOnDemandRecordCount]);

  const handleInfoClick = () => {
    const infoTabIndex = items?.findIndex((x) => x?.label === 'Info');
    setTab(infoTabIndex);
  };

  const handleBookmarkClick = () => {
    setOpenModal(true);
  };

  const SiteCardCOmponent = ({
    moreInfoTabActive,
    showCard = false,
    className = '',
    isInfo = false
  }) =>
    showCard ? (
      <div className={`marginClass ${className}`} ref={siteCardRef}>
        <SiteCard
          siteDetailsCard
          chips={true}
          title={area}
          subtitle={name}
          hideLocation={true}
          dataPoints={dataPoints || []}
          allTasksForSite={tasks}
          allDoneTasks={allDoneTasks}
          isOverDue={isOverDue}
          status={status}
          arrayText={arrayText}
          open={open}
          setOpen={setOpen}
          moreInfo={moreInfo}
          childrenList={children}
          addChild={!moreInfo && '+ child sites'}
          allOnDemandRecordCount={allOnDemandRecordCount + (allEditEquipmentAssets?.length || 0)}
          equipmentCount={equipmentCount}
          structureCount={structureCount}
          pipelineCount={pipelineCount}
          onInfoCLick={handleInfoClick}
          onBookmarkCLick={handleBookmarkClick}
          infoTabActive={moreInfoTabActive}
          toDesktopLink={link && width >= 768 ? link : undefined}
          // otherTabsView={allTask || checks || workOrder || workRequest || inspections}
          showInfoIcon={
            MoreInfoDataPointsMap &&
            !(allTask || checks || workOrder || workRequest || inspections || newOrEditAssets)
          }
          className={`${isInfo && style.borderBottomNone}`}
        />
      </div>
    ) : (
      <></>
    );

  const tabIsActive = useMemo(() => {
    return (
      isFilterActiveForEquipments ||
      isFilterActiveForStructure ||
      isFilterActiveForPipeline ||
      isFilterActiveForChildSites ||
      false
    );
  }, [
    isFilterActiveForEquipments,
    isFilterActiveForStructure,
    isFilterActiveForPipeline,
    isFilterActiveForChildSites
  ]);

  useEffect(() => {
    const elements = document.getElementsByClassName('react-swipeable-view-container');

    for (let i = 0; i < elements.length; i++) {
      let element = elements[i];
      if (tasks?.length > 0) {
        if (tab === 1) element.style.transform = tabIsActive ? 'none' : 'translate(-100%, 0px)';
        if (tab === 2) element.style.transform = tabIsActive ? 'none' : 'translate(-200%, 0px)';
        if (tab === 3) element.style.transform = tabIsActive ? 'none' : 'translate(-300%, 0px)';
        if (tab === 4) element.style.transform = tabIsActive ? 'none' : 'translate(-400%, 0px)';
      } else {
        if (parseInt(tab) === 0)
          element.style.transform = tabIsActive ? 'none' : 'translate(0px, 0px)';
        if (tab === 1) element.style.transform = tabIsActive ? 'none' : 'translate(-100%, 0px)';
        if (tab === 2) element.style.transform = tabIsActive ? 'none' : 'translate(-200%, 0px)';
        if (tab === 3) element.style.transform = tabIsActive ? 'none' : 'translate(-300%, 0px)';
      }
    }
  }, [tabIsActive]);

  const items = useMemo(() => {
    return !isLoading
      ? [
          ...(tasks?.length > 0 || allOnDemandRecordCount > 0 || allEditEquipmentAssets?.length > 0
            ? [
                {
                  label: `Tasks + records (${
                    tasks.length + allOnDemandRecordCount + allEditEquipmentAssets?.length
                  })`,
                  keyName: 'tasks',
                  children: (
                    <ParentSite
                      setChecks={setChecks}
                      setAllTask={setAllTask}
                      setWorkOrder={setWorkOrder}
                      setInspections={setInspections}
                      allWorkOrderForSite={allWorkOrders}
                      allDoneWorkOrder={allDoneWorkOrders}
                      allInspectionForSite={allInspections}
                      allDoneInspection={allDoneInspections}
                      allCheckForSite={allChecks}
                      allDoneChecks={allDoneChecks}
                      allTasksForSite={tasks}
                      allDoneTasks={allDoneTasks}
                      isOverDue={isOverDue}
                      isWorkOrderOverDue={isWorkOrderOverDue}
                      isInspectionOverDue={isInspectionOverDue}
                      isCheckOverDue={isCheckOverDue}
                      setWorkRequest={setWorkRequest}
                      setHseEvent={setHseEvent}
                      newWorkRequestList={newWorkRequestList}
                      hseEvents={hseEvents}
                      onDemandRecordList={allOnDemandRecordCount + allEditEquipmentAssets?.length}
                      newWorkOrderList={parentSiteDetails?.newWorkOrderList || []}
                      newInspectionList={parentSiteDetails?.newInspectionList || []}
                      newChecksList={parentSiteDetails?.newChecksList || []}
                      setNewOrEditAssets={setNewOrEditAssets}
                      allEditEquipmentAssets={allEditEquipmentAssets}
                      card={() => (
                        <SiteCardCOmponent
                          moreInfoTabActive={false}
                          showCard={width <= 768 ? true : false}
                          className={`mb16 ${style.pt0}`}
                        />
                      )}
                    />
                  )
                }
              ]
            : []),

          ...(children?.length > 0
            ? [
                {
                  label: `Child sites (${children?.length})`,
                  keyName: 'site',
                  children: (
                    <SiteListing
                      assetDetailsSiteLists={children}
                      isFilterActiveHeader={isFilterActiveForChildSites}
                      setFilterActiveHeader={setFilterActiveForChildSites}
                      from={location.pathname}
                      siteId={id}
                      detailPage
                      detailsPageBookmarks={searchParams.get('bookmarkKeyName') === 'site'}
                      navLabel="Child sites"
                      card={() => (
                        <SiteCardCOmponent
                          moreInfoTabActive={false}
                          className={` ${style.pt0}`}
                          showCard={
                            !(
                              tasks?.length > 0 ||
                              allOnDemandRecordCount > 0 ||
                              allEditEquipmentAssets?.length > 0
                            ) && width <= 768
                              ? true
                              : false
                          }
                        />
                      )}
                    />
                  )
                }
              ]
            : []),

          ...(structures?.length > 0
            ? [
                {
                  label: `Structure (${structures?.length})`,
                  keyName: 'structure',
                  children: (
                    <StructureListing
                      assetDetailsStructureLists={structureAllFieldData}
                      isFilterActiveHeader={isFilterActiveForStructure}
                      setFilterActiveHeader={setFilterActiveForStructure}
                      from={location.pathname}
                      siteId={id}
                      detailPage
                      detailsPageBookmarks={searchParams.get('bookmarkKeyName') === 'structure'}
                      card={() => (
                        <SiteCardCOmponent
                          moreInfoTabActive={false}
                          className={` ${style.pt0}`}
                          showCard={
                            !(
                              tasks?.length > 0 ||
                              allOnDemandRecordCount > 0 ||
                              allEditEquipmentAssets?.length > 0 ||
                              children?.length > 0
                            ) && width <= 768
                              ? true
                              : false
                          }
                        />
                      )}
                    />
                  )
                }
              ]
            : []),

          ...(equipments?.length > 0
            ? [
                {
                  label: `Equipment (${equipments?.length})`,
                  keyName: 'equipment',
                  children: (
                    <EquipmentListing
                      assetDetailsOfEquipmentLists={equipmentsWithWO}
                      isFilterActiveHeader={isFilterActiveForEquipments}
                      setFilterActiveHeader={setFilterActiveForEquipments}
                      from={location.pathname}
                      siteId={id}
                      detailPage
                      detailsPageBookmarks={searchParams.get('bookmarkKeyName') === 'equipment'}
                      card={() => (
                        <SiteCardCOmponent
                          moreInfoTabActive={false}
                          className={` ${style.pt0}`}
                          showCard={
                            !(
                              tasks?.length > 0 ||
                              allOnDemandRecordCount > 0 ||
                              allEditEquipmentAssets?.length > 0 ||
                              children?.length > 0 ||
                              structures?.length > 0
                            ) && width <= 768
                              ? true
                              : false
                          }
                        />
                      )}
                    />
                  )
                }
              ]
            : []),

          ...(pipelines?.length > 0
            ? [
                {
                  label: `Pipeline (${pipelines?.length})`,
                  keyName: 'pipeline',
                  children: (
                    <PipelineListing
                      assetDetailsPipelineLists={pipelines}
                      isFilterActiveHeader={isFilterActiveForPipeline}
                      setFilterActiveHeader={setFilterActiveForPipeline}
                      from={location.pathname}
                      siteId={id}
                      detailPage
                      detailsPageBookmarks={searchParams.get('bookmarkKeyName') === 'pipeline'}
                      card={() => (
                        <SiteCardCOmponent
                          moreInfoTabActive={false}
                          className={` ${style.pt0}`}
                          showCard={
                            !(
                              tasks?.length > 0 ||
                              allOnDemandRecordCount > 0 ||
                              allEditEquipmentAssets?.length > 0 ||
                              children?.length > 0 ||
                              structures?.length > 0 ||
                              equipments?.length > 0
                            ) && width <= 768
                              ? true
                              : false
                          }
                        />
                      )}
                    />
                  )
                }
              ]
            : []),

          ...(MoreInfoDataPointsMap
            ? [
                {
                  label: `Info`,
                  keyName: 'info',
                  children: (
                    <>
                      <div className={style.infoTab} id="site-info-tab">
                        <SiteCardCOmponent
                          moreInfoTabActive={true}
                          showCard={width <= 768 ? true : false}
                          className={`${style.pt0}`}
                          isInfo
                        />
                        <PatentSiteDetailsInfo MoreInfoDataPointsMap={MoreInfoDataPointsMap} />
                      </div>
                    </>
                  )
                }
              ]
            : []),

          ...([...children, parentSiteDetails]?.length > 0 &&
          coordinatesListOfChild?.allSiteCoordinatesList?.length > 0 &&
          !_.isNull(parentSiteDetails?.distance) &&
          isOnline
            ? [
                {
                  label: `Map`,
                  keyName: 'map',
                  children: (
                    <MapListing
                      siteData={[
                        parentSiteDetails,
                        ...children.filter((x) => !_.isNull(x?.distance))
                      ]}
                      allSiteCoordinatesList={coordinatesListOfChild.allSiteCoordinatesList}
                      detailPage={true}
                      siteChildrenList={[parentSiteDetails, ...(children || [])]}
                      parentSiteDetailsPage={true}
                    />
                  )
                }
              ]
            : [])
        ]
      : [];
  }, [
    isLoading,
    itemsMap,
    isFilterActive,
    isFilterActiveForEquipments,
    isFilterActiveForPipeline,
    isFilterActiveForStructure,
    isFilterActiveForChildSites,
    allDoneTasks,
    tasks,
    children,
    coordinatesListOfChild,
    newWorkRequestList,
    parentSiteDetails,
    isOnline
  ]);

  useEffect(() => {
    if (items?.length > 0) {
      const params = new URLSearchParams(location.search);
      const currentTab = parseInt(tab);
      if (items[currentTab]?.keyName === 'tasks') {
        if (allTask) params.set('currentOpenTaskTab', 'allTask');
        else if (workOrder) params.set('currentOpenTaskTab', 'workOrder');
        else if (inspections) params.set('currentOpenTaskTab', 'inspections');
        else if (workRequest) params.set('currentOpenTaskTab', 'workRequest');
        else if (hseEvent) params.set('currentOpenTaskTab', 'hseEvent');
        else if (checks) params.set('currentOpenTaskTab', 'checks');
        else if (newOrEditAssets) params.set('currentOpenTaskTab', 'newOrEditAssets');
        else if (
          !workOrder &&
          !allTask &&
          !inspections &&
          !workRequest &&
          !hseEvent &&
          !checks &&
          !newOrEditAssets
        )
          params.delete('currentOpenTaskTab');
      }

      params.set('tab', tab);
      params.set(
        'virtualScrollTab',
        currentTab === 0 && items[currentTab]?.keyName === 'tasks'
          ? -1
          : tasks?.length > 0
          ? currentTab - 1
          : currentTab
      );
      params.set('keyName', items[tab]?.keyName);
      const tabParams = '?' + params.toString();
      const prevRoute = `${location.pathname}${tabParams}`;
      navigate(prevRoute, { replace: true });
    }
  }, [tab, items, workOrder, allTask, inspections, workRequest, hseEvent, newOrEditAssets]);

  useEffect(() => {
    if (!isLoading && bookmarkTab) setTab(bookmarkTab);
  }, [isLoading, bookmarkTab]);

  const onParentClickEvent = ({ workOrderTab, workOrderSortTab }) => {
    dispatch?.(
      backHistoryActions.setParentSiteDetailsPageHistory({
        workOrder,
        parentSiteDetailsTab: tab,
        workOrderTab,
        workOrderSortTab,
        newOrEditAssets
      })
    );
  };

  const pageHeaderTitle = useMemo(() => {
    return `${
      bookmarkName
        ? bookmarkName
        : moreInfo
        ? 'Site Information'
        : allTask
        ? `Tasks, records (${
            tasks?.length > 0 || allOnDemandRecordCount || allEditEquipmentAssets?.length > 0
              ? tasks?.length + allOnDemandRecordCount + allEditEquipmentAssets?.length
              : ''
          })`
        : checks
        ? `Checks (${allChecks?.length > 0 ? allChecks?.length : ''})`
        : inspections
        ? `Inspections (${allInspections?.length > 0 ? allInspections?.length : ''})`
        : workOrder
        ? `Work orders (${
            allWorkOrders?.length > 0 || parentSiteDetails?.newWorkOrderList?.length
              ? allWorkOrders?.length + parentSiteDetails?.newWorkOrderList?.length
              : ''
          })`
        : workRequest
        ? `Work requests (${newWorkRequestList?.length > 0 ? newWorkRequestList.length : ''})`
        : newOrEditAssets
        ? `New or edited assets (${
            allEditEquipmentAssets?.length > 0 ? allEditEquipmentAssets?.length : ''
          })`
        : hseEvent
        ? `HSE events (${hseEvents?.length > 0 ? hseEvents.length : ''})`
        : 'Parent site'
    }`;
  }, [
    bookmarkName,
    moreInfo,
    allTask,
    checks,
    inspections,
    workOrder,
    tasks,
    workRequest,
    hseEvent,
    newWorkRequestList,
    parentSiteDetails,
    allOnDemandRecordCount,
    newOrEditAssets,
    allEditEquipmentAssets
  ]);

  const pageHeaderClickEvent = () => {
    if (selectedTaskType) {
      navigate(-1);
    } else {
      workOrder && dispatch?.(filterActions.setTaskFilters(null));
      if (moreInfo) setMoreInfo(false);
      else if (allTask) setAllTask(false);
      else if (checks) setChecks(false);
      else if (inspections) setInspections(false);
      else if (workOrder) setWorkOrder(false);
      else if (workRequest) setWorkRequest(false);
      else if (hseEvent) setHseEvent(false);
      else if (newOrEditAssets) setNewOrEditAssets(false);

      dispatch?.(backHistoryActions.setParentSiteDetailsPageHistory(null));
    }
  };

  const isFilterIconIsRequired = useMemo(() => {
    return (tab !== '0' &&
      !items[tab]?.keyName?.toLowerCase()?.includes('tasks') &&
      !items[tab]?.keyName?.toLowerCase()?.includes('map') &&
      !items[tab]?.keyName?.toLowerCase()?.includes('info')) ||
      moreInfo ||
      allTask ||
      checks ||
      inspections ||
      workOrder ||
      workRequest ||
      hseEvent ||
      newOrEditAssets
      ? items[tab]?.keyName === 'equipment'
        ? setFilterActiveForEquipments
        : items[tab]?.keyName === 'structure'
        ? setFilterActiveForStructure
        : items[tab]?.keyName === 'pipeline'
        ? setFilterActiveForPipeline
        : items[tab]?.keyName === 'site'
        ? setFilterActiveForChildSites
        : setFilterActive
      : false;
  }, [tab, items, allTask, checks, inspections, workOrder, workRequest, hseEvent, newOrEditAssets]);

  const filtersCount = useMemo(() => {
    return items[tab]?.keyName === 'equipment'
      ? equipmentFilterCount
      : items[tab]?.keyName === 'structure'
      ? structureFilterCount
      : items[tab]?.keyName === 'pipeline'
      ? pipelineFilterCount
      : items[tab]?.keyName === 'site'
      ? sitesFilterCount
      : tasksFilterCount;
  }, [
    tab,
    items,
    equipmentFilterCount,
    structureFilterCount,
    pipelineFilterCount,
    sitesFilterCount,
    tasksFilterCount
  ]);

  const siteDetailsPageClassNames = useMemo(() => {
    return `${moreInfo || selectTask ? style.pageListingsWithMoreInfo : style.pageListings} ${
      style.noScroll
    } ${
      (workOrder || allTask || checks || inspections || workRequest || hseEvent) && style.noScroll
    }`;
  }, [workOrder, allTask, checks, inspections, moreInfo, selectTask, workRequest, hseEvent]);

  useEffect(() => {
    dispatch?.(siteMapActions.resetMapState());
  }, [dispatch]);

  const siteNotFoundInBookmark = useMemo(() => {
    if (bookmarkName && !isLoading) {
      if (name && area) return false;
      else return true;
    }
    return false;
  }, [bookmarkName, isLoading, name, area]);

  // useEffect(() => {
  //   const googleMapOfAssetDetailPage = document?.getElementById('google-map-asset-page');
  //   const siteCardContainerBottom = document?.getElementById('site-card-container-bottom');
  //   const siteCardOffsetHeight = siteCardRef?.current?.offsetHeight || 0;

  //   if (!isLoading && googleMapOfAssetDetailPage && siteCardOffsetHeight) {
  //     const heightOfNavbarAndTabsMenu = width >= 768 ? 94 + 32 + 16 : 86 + 32 + 16 + 84;
  //     const heightOfMap = heightOfNavbarAndTabsMenu + parseInt(siteCardOffsetHeight);
  //     googleMapOfAssetDetailPage.style.height = `calc(100vh - ${heightOfMap}px)`;
  //     googleMapOfAssetDetailPage?.setAttribute('data-height', heightOfMap);
  //   }

  //   if (siteCardContainerBottom && selectedSiteIdForMapView.siteId && googleMapOfAssetDetailPage) {
  //     const siteCardContainerBottomSelectedSite =
  //       document?.getElementById(`site-card-container-bottom-0`)?.offsetHeight || 0;

  //     const currentScrollPosition =
  //       googleMapOfAssetDetailPage?.getAttribute('current-scroll-position') || 0;

  //     const heightOfNavbarAndTabsMenu =
  //       (width >= 768 ? 94 + 32 + 16 : 86 + 32 + 16 + 84) + siteCardContainerBottomSelectedSite;
  //     const heightOfMap =
  //       heightOfNavbarAndTabsMenu + parseInt(siteCardOffsetHeight) - currentScrollPosition;
  //     googleMapOfAssetDetailPage.style.height = `calc(100vh - ${heightOfMap}px)`;
  //     googleMapOfAssetDetailPage?.setAttribute('data-height', heightOfMap);
  //   } else {
  //     if (googleMapOfAssetDetailPage) {
  //       const previousHeight = googleMapOfAssetDetailPage?.getAttribute('data-height') || 0;
  //       const currentScrollPosition =
  //         googleMapOfAssetDetailPage?.getAttribute('current-scroll-position') || 0;

  //       googleMapOfAssetDetailPage.style.height = `calc(100vh - ${
  //         previousHeight - currentScrollPosition
  //       }px)`;
  //     }
  //   }
  // }, [
  //   isLoading,
  //   siteCardRef?.current,
  //   width,
  //   document?.getElementById('google-map-asset-page'),
  //   document?.getElementById('site-card-container-bottom'),
  //   selectedSiteIdForMapView
  // ]);

  const lastScrollPosition = useRef(0);

  useEffect(() => {
    if (selectedSiteIdForMapView?.siteId) {
      dispatch?.(
        siteMapActions.setSelectedSiteIdOnMapView({
          siteId: null,
          siteIndex: -1
        })
      );
    }
  }, [tab, dispatch]);

  const allInspectionsList = useMemo(() => {
    return [
      ...(parentSiteDetails?.allInspections || []),
      ...(parentSiteDetails?.newInspectionList || [])
    ];
  }, [inspections, parentSiteDetails]);

  const allTasksList = useMemo(() => {
    return [
      ...(parentSiteDetails?.tasks || []),
      ...(parentSiteDetails.newWorkOrderList || []),
      ...(parentSiteDetails?.hseEvents || []),
      ...(parentSiteDetails?.newWorkRequestList || []),
      ...(parentSiteDetails?.allEditEquipmentAssets || [])
    ];
  }, [allTask, parentSiteDetails]);

  const allWorkOrderList = useMemo(() => {
    return [...(allWorkOrders || []), ...(parentSiteDetails?.newWorkOrderList || [])];
  }, [workOrder, parentSiteDetails, allWorkOrders]);

  const allChecksList = useMemo(() => {
    return [...(allChecks || []), ...(parentSiteDetails?.newChecksList || [])];
  }, [checks, parentSiteDetails, allChecks]);

  useEffect(() => {
    if (!isLoading && !name && !status) {
      navigate('/error?assetNotFound=true');
    }
  }, [isLoading, name, status]);

  const onDeleteBookmark = async () => {
    try {
      if (!bookmarkId) {
        ToasterUtil.showError('Unable to delete bookmark: Bookmark ID is missing.');
        return;
      }

      await BookmarksService.deleteBookmarks({ bookmarkId: bookmarkId });

      dispatch?.(bookmarksActions.setBookmarkValidity(true));

      ToasterUtil.showSuccess('Bookmark deleted successfully');

      const params = new URLSearchParams(location.search);

      params.set('from', 'site');
      params.delete('bookmarkName');
      params.delete('bookmarkId');

      setDeleteBookmark(false);

      const newUrl = `${location.pathname}?${params.toString()}`;

      navigate(newUrl, { replace: true });
    } catch (error) {
      ToasterUtil.showError('Failed to delete bookmark');
    }
  };

  const { moreInfoTabActive, isTasksTabActive } = useMemo(() => {
    const parseTab = parseInt(tab);
    return {
      moreInfoTabActive: items[parseTab]?.keyName === 'info' ? true : false,
      isTasksTabActive: items[parseTab]?.keyName === 'tasks' ? true : false
    };
  }, [items, tab]);

  useEffect(() => {
    if (!isLoading && selectedTaskType) {
      if (selectedTaskType === 'check' && allChecks?.length) setChecks(true);
      else if (selectedTaskType === 'inspection' && allInspections.length) setInspections(true);
      else if (
        selectedTaskType === 'workOrder' &&
        (allWorkOrders.length ||
          allDoneWorkOrders?.length ||
          parentSiteDetails?.newWorkOrderList?.length)
      )
        setWorkOrder(true);
      else if (selectedTaskType === 'workRequest' && newWorkRequestList?.length)
        setWorkRequest(true);
      else if (selectedTaskType === 'hseEvents' && hseEvents?.length) setHseEvent(true);
      else if (selectedTaskType === 'NewOrEditAsset' && allEditEquipmentAssets?.length)
        setNewOrEditAssets(true);
    }
  }, [
    selectedTaskType,
    isLoading,
    allChecks,
    allDoneWorkOrders,
    allInspections,
    allWorkOrders,
    newWorkRequestList,
    hseEvents,
    allEditEquipmentAssets,
    parentSiteDetails?.newWorkOrderList
  ]);

  const swipeable = document.getElementById('react-swipeable-view-container') || null;
  const siteInfoTab = document.getElementById('site-info-tab') || null;
  const taskInfoTab = document.getElementById('tasks-info-tab') || null;

  useEffect(() => {
    // const currentScrollPosition = mainDivRef?.current?.scrollTop;
    // const siteCardOffsetHeight = siteCardRef?.current?.offsetHeight || 0;

    // if (siteCardRef?.current && mainDivRef?.current) {
    //   const container =
    //     moreInfoTabActive && siteInfoTab
    //       ? siteInfoTab
    //       : isTasksTabActive && taskInfoTab
    //       ? taskInfoTab
    //       : null;

    //   if (container && (moreInfoTabActive || isTasksTabActive)) {
    //     const topHeaderHeight = width > 769 ? 94 : 86;
    //     const siteCardAndMarginCalculate = siteCardOffsetHeight || 0;
    //     const navigationTabsHeight = 33;
    //     const bottomFooterHeight = width > 769 ? 0 : 84;

    //     const totalHeight =
    //       topHeaderHeight + siteCardAndMarginCalculate + bottomFooterHeight + navigationTabsHeight;

    //     container.style.height = `calc(100dvh - ${totalHeight - currentScrollPosition}px)`;
    //   }
    if (swipeable) {
      if (moreInfoTabActive) {
        swipeable.style.background = '#ffffff';
      } else {
        swipeable.style.background = '';
      }
    }
  }, [
    // mainDivRef,
    // tab,
    moreInfoTabActive,
    swipeable
    // isTasksTabActive,
    // siteInfoTab,
    // taskInfoTab,
    // isTasksTabActive,
    // siteCardRef
  ]);

  const onScrollParentEvent = (event) => {
    const siteCardOffsetHeight = siteCardRef?.current?.offsetHeight || 0;
    const currentScrollPosition = event.target.scrollTop || 0;

    const googleMapOfAssetDetailPage = document?.getElementById('google-map-asset-page');
    const siteCardContainerBottom = document?.getElementById('site-card-container-bottom');

    const siteCardContainerBottomSelectedSite =
      (siteCardContainerBottom &&
        selectedSiteIdForMapView.siteId &&
        siteCardContainerBottom.getAttribute('style') &&
        document?.getElementById(`site-card-container-bottom-${selectedSiteIdForMapView.siteIndex}`)
          ?.offsetHeight) ||
      0;

    const heightOfNavbarAndTabsMenu =
      width >= 768 ? 94 + 32 + 16 : 86 + 32 + 16 + 84 + siteCardContainerBottomSelectedSite;

    if (
      currentScrollPosition > lastScrollPosition.current &&
      googleMapOfAssetDetailPage &&
      items[tab]?.keyName?.toLowerCase()?.includes('map')
    ) {
      // check if user is scrolling up
      const heightOfMap =
        heightOfNavbarAndTabsMenu +
        parseInt(siteCardOffsetHeight) -
        parseInt(currentScrollPosition);

      googleMapOfAssetDetailPage.style.height = `calc(100vh - ${heightOfMap}px)`;
      googleMapOfAssetDetailPage?.setAttribute('data-height', heightOfMap);
      googleMapOfAssetDetailPage?.setAttribute('current-scroll-position', currentScrollPosition);
    } else if (currentScrollPosition < lastScrollPosition.current && googleMapOfAssetDetailPage) {
      // check if user is scrolling down

      const heightOfMap =
        heightOfNavbarAndTabsMenu +
        (parseInt(siteCardOffsetHeight) - parseInt(currentScrollPosition));

      googleMapOfAssetDetailPage.style.height = `calc(100vh - ${heightOfMap}px)`;
      googleMapOfAssetDetailPage?.setAttribute('data-height', heightOfMap);
      googleMapOfAssetDetailPage?.setAttribute('current-scroll-position', currentScrollPosition);
    }

    // const isTasksTabActive = items[parseInt(tab)]?.keyName === 'tasks' ? true : false;

    if (siteCardRef?.current) {
      const topHeaderHeight = width > 769 ? 94 : 86;
      const siteCardAndMarginCalculate = siteCardOffsetHeight || 0;
      const navigationTabsHeight = 33;
      const bottomFooterHeight = width > 769 ? 0 : 84;
      const totalHeight =
        topHeaderHeight + siteCardAndMarginCalculate + bottomFooterHeight + navigationTabsHeight;

      const container =
        moreInfoTabActive && siteInfoTab
          ? siteInfoTab
          : isTasksTabActive && taskInfoTab
          ? taskInfoTab
          : null;

      if (container && (moreInfoTabActive || isTasksTabActive)) {
        container.style.height = `calc(100dvh - ${totalHeight - currentScrollPosition}px)`;
      }

      const containerIds = [
        'structure-listing-pages-container',
        'site-listing-pages-container',
        'pipeline-listing-pages-container',
        'equipment-listing-pages-container'
      ];
      containerIds.forEach((id) => {
        const container = document.getElementById(id);
        if (container) {
          container.style.height = `calc(100dvh - ${totalHeight - currentScrollPosition + 10}px)`;
        }
      });
    }

    const tasksRecordsListsTabs = document?.querySelectorAll('#tasksRecordsListsTabs');
    for (let a = 0; a < tasksRecordsListsTabs.length; a++) {
      const totalHeight =
        tasksRecordsListsTabs[a]?.getAttribute('data-pageHeightOnDetailsPage') || 0;
      tasksRecordsListsTabs[a].style.height = `calc(100svh  - ${
        totalHeight - currentScrollPosition
      }px)`;
    }

    lastScrollPosition.current = currentScrollPosition;
  };

  return (
    <>
      {!isLoading && siteNotFoundInBookmark ? (
        <BookmarkErrorScreen />
      ) : (
        <PageContainer moreInfo={moreInfo}>
          <PageHeader
            disableBack={
              allTask ||
              checks ||
              inspections ||
              workOrder ||
              workRequest ||
              hseEvent ||
              checks ||
              newOrEditAssets
            }
            {...(isFilterIconIsRequired && {
              setFilterActive: isFilterIconIsRequired
            })}
            title={pageHeaderTitle}
            showFilter={pageHeaderTitle?.toLowerCase()?.includes('information')}
            onClick={pageHeaderClickEvent}
            count={filtersCount}
            ref={assetDetailPageHeaderRef}
          />
          {isLoading ? (
            <LoadingRound className={style.loading} />
          ) : (
            <div
              className={siteDetailsPageClassNames}
              style={{ position: 'relative', paddingBottom: '0px', marginTop: '0px' }}
              id="assetDetailPage"
              ref={mainDivRef}
              onScroll={onScrollParentEvent}
            >
              <SiteCardCOmponent
                moreInfoTabActive={moreInfoTabActive}
                showCard={width > 768 ? true : false}
              />
              {moreInfo ? (
                <PatentSiteDetailsInfo MoreInfoDataPointsMap={MoreInfoDataPointsMap} />
              ) : (
                <div className="flex relative">
                  <div
                    style={{
                      marginTop: '0px',
                      overflow: 'hidden',
                      paddingBottom: '0px'
                    }}
                    className={`${
                      moreInfo ? style.pageListingsWithMoreInfo : style.pageListings
                    } mobileMargin`}
                  >
                    {allTask ||
                    inspections ||
                    workOrder ||
                    hseEvent ||
                    workRequest ||
                    checks ||
                    newOrEditAssets ? (
                      <WorkOrder
                        tasks={
                          inspections
                            ? allInspectionsList
                            : allTask
                            ? allTasksList
                            : workOrder
                            ? allWorkOrderList
                            : workRequest
                            ? newWorkRequestList
                            : hseEvent
                            ? hseEvents
                            : checks
                            ? allChecksList
                            : newOrEditAssets
                            ? allEditEquipmentAssets
                            : []
                        }
                        workRequest={workRequest}
                        newOrEditAssets={newOrEditAssets}
                        isFilterActiveHeader={isFilterActive}
                        setFilterActiveHeader={setFilterActive}
                        selectTodoOrInProgressStatus={!_.isEmpty(bookmarkName) ? true : false}
                        workOrderSelectedTab={parentSiteDetailsInfo?.workOrderTab || null}
                        workOrderSortTab={parentSiteDetailsInfo?.workOrderSortTab || null}
                        onParentClickEvent={onParentClickEvent}
                        parentSiteDetails
                        hseEvent={hseEvent}
                        filterType={
                          allTask
                            ? 'all'
                            : inspections
                            ? 'inspection'
                            : workOrder
                            ? 'workOrder'
                            : workRequest
                            ? 'workRequest'
                            : hseEvent
                            ? 'hseEvents'
                            : checks
                            ? 'check'
                            : newOrEditAssets
                            ? 'newOrEditAssets'
                            : null
                        }
                        asset="sites"
                      />
                    ) : items?.length > 0 ? (
                      <div
                        ref={swipeAbleMenuRef}
                        id="assetDetailsNavigation"
                        className={`assetDetailNavNotFixed`}
                      >
                        <NewTabs
                          active={tab}
                          tabs={items}
                          setActive={(tab) => {
                            setTab(tab);
                          }}
                          className={style.mobileMargin}
                          swipeAbleNavbarWhiteBackground={moreInfoTabActive ? true : false}
                        />
                      </div>
                    ) : (
                      <>
                        <div
                          className="no-result"
                          style={{ flex: 1, justifyContent: 'flex-start', gap: '20px' }}
                        >
                          <div>No results</div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {openModal && (
            <SiteBookmark
              open={openModal}
              setOpen={(value) => {
                setOpenModal(value);
              }}
              placeholder="My favourite truck"
              type="Parent Site Details"
              siteId={id || null}
              editMode={bookmarkId}
              bookmarkPrefillName={bookmarkName}
              isBookmarkAtAGlance={isBookmarkAtAGlance}
              onDelete={() => {
                setDeleteBookmark(true);
                setOpenModal(false);
              }}
            />
          )}
          {deleteBookmark && (
            <DeleteModal
              open={deleteBookmark}
              setOpen={setDeleteBookmark}
              handleDelete={onDeleteBookmark}
              confirmDeletionBookmark
              recordType="Bookmark"
            />
          )}

          {[...(children || []), parentSiteDetails]?.length > 0 &&
            coordinatesListOfChild?.allSiteCoordinatesList?.length > 0 &&
            width < 1025 && (
              <SiteCardMenuBottom
                selectedSiteIdForMapView={selectedSiteIdForMapView}
                // siteChildrenList={[...(children || []), parentSiteDetails]}
                siteChildrenList={[parentSiteDetails, ...(children || [])]}
                openMapMenu={true}
                picklistsMap={picklistsMap}
                tab={tab}
              />
            )}
        </PageContainer>
      )}
    </>
  );
};

export default memo(ParentSiteDetails);
